import type { BlameHunk, CommentBlock, HighlightedLines, Remote } from 'codesearch-backend'
import type { VListHandle } from 'virtua/solid'
import type { TRange } from '../utils/pattern.ts'
import { createMemo, createSignal, onMount, Show } from 'solid-js'
import { VList } from 'virtua/solid'
import { cn } from '../utils/cn.ts'
import { isoDate } from '../utils/format.ts'
import { CodeLine, lineTokens } from './CodeLine.tsx'
import { CommitPopover } from './CommitPopover.tsx'
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/HoverCard.tsx'

export interface TCodeHandle {
  scrollToLine: (line: number) => void
}

interface TBlameRow {
  hunk: BlameHunk
  /** which commit this is in the file's history, oldest first - code search's `#N` */
  ordinal: number
  /** hunk parity, for the alternating bands that make hunk boundaries visible */
  band: number
  /** the gutter only prints on a hunk's first line, the rest is empty by design */
  starts: boolean
}

/*
  line number (1-based) -> what the gutter shows there. the `#N` ordinal is code
  search's: commits touching this file numbered from the oldest, which reads far
  better than a hash when you're scanning a column of them
*/
function blameRows(hunks: BlameHunk[], lines: number): (TBlameRow | undefined)[] {
  const ordinals = new Map<string, number>()
  for (const [i, commit] of [...new Set([...hunks].sort((a, b) => a.time - b.time).map(it => it.commit))].entries()) {
    ordinals.set(commit, i + 1)
  }

  const out: (TBlameRow | undefined)[] = Array.from({ length: lines })
  const ordered = [...hunks].sort((a, b) => a.start - b.start)

  for (const [band, hunk] of ordered.entries()) {
    for (let i = 0; i < hunk.len; i++) {
      const index = hunk.start - 1 + i
      if (index >= lines) break
      out[index] = {
        hunk,
        ordinal: ordinals.get(hunk.commit) ?? 0,
        band: band % 2,
        starts: i === 0,
      }
    }
  }

  return out
}

/*
  every line a doc comment covers points at that comment, so hovering anywhere in it
  brings the same card up - a link written across three lines of a jsdoc block is
  still one link, and having to find the line the `](` landed on would be silly
*/
function commentRows(blocks: CommentBlock[], lines: number): (CommentBlock | undefined)[] {
  const out: (CommentBlock | undefined)[] = Array.from({ length: lines })
  for (const block of blocks) {
    for (let line = block.start; line <= Math.min(block.end, lines); line++) out[line - 1] = block
  }
  return out
}

export function CodeBody(props: {
  content: string
  tokens: HighlightedLines | null
  /** doc comments rendered as markdown on the server, shown on hover */
  comments?: CommentBlock[]
  /** match ranges per line index; absent lines simply have none */
  ranges?: Map<number, TRange[]>
  currentLine?: number
  currentMatch?: number
  activeLine?: number
  blame?: BlameHunk[]
  /** the index being viewed, and where its origin lives - for the commit card */
  repo: string
  remote?: Remote | null
  onBlameBefore?: (sha: string) => void
  fontSize: number
  /** the 80-column ruler, code search's `8` */
  columnGuide?: boolean
  onLineClick: (line: number) => void
  /** where a line number points, so middle- and ctrl-click behave like links */
  lineHref?: (line: number) => string
  ref?: (handle: TCodeHandle) => void
}) {
  const lines = createMemo(() => props.content.split('\n'))
  /* the gutter is as wide as the widest number it will ever hold, so it doesn't
     twitch a character wider as the virtualiser scrolls past line 999 */
  const gutterWidth = createMemo(() => `calc(${String(lines().length).length}ch + 8px)`)
  const blame = createMemo(() => (props.blame ? blameRows(props.blame, lines().length) : undefined))
  const comments = createMemo(() => (props.comments?.length ? commentRows(props.comments, lines().length) : undefined))
  /* the comment the cursor is standing in, so the whole block lights up rather than
     the one line under the pointer - a doc comment is read as a paragraph */
  const [hovered, setHovered] = createSignal<CommentBlock | null>(null)

  let list: VListHandle | undefined
  const handle: TCodeHandle = {
    scrollToLine: (line: number) => list?.scrollToIndex(Math.max(0, line - 1), { align: 'center' }),
  }
  // handed up once and never replaced; it closes over `list`, which is the part
  // that actually changes
  onMount(() => props.ref?.(handle))

  return (
    <VList
      ref={it => (list = it)}
      data={lines()}
      class="code-panel h-full"
      style={{ 'font-size': `${props.fontSize}px` }}
    >
      {(line, i) => {
        const row = () => blame()?.[i()]
        const comment = () => comments()?.[i()]
        const codeLine = () => (
          <CodeLine
            tokens={lineTokens(props.tokens, i(), line)}
            ranges={props.ranges?.get(i() + 1)}
            currentMatch={props.currentLine === i() + 1 ? props.currentMatch : undefined}
          />
        )

        /* leaving one line of a block for the next one is not leaving the block, so
           the row the pointer moved onto decides whether the highlight drops */
        function leave(event: MouseEvent) {
          const next = (event.relatedTarget as Element | null)?.closest?.('[data-line]')
          const line = next ? Number((next as HTMLElement).dataset.line) : Number.NaN
          const block = comment()
          if (block && line >= block.start && line <= block.end) return
          setHovered(it => (it === block ? null : it))
        }

        return (
          <div
            data-line={i() + 1}
            onMouseEnter={() => setHovered(comment() ?? null)}
            onMouseLeave={leave}
            aria-current={props.activeLine === i() + 1 ? 'true' : undefined}
            /*
              w-max min-w-full: the row is as wide as its longest content so the
              viewport scrolls sideways, but never narrower than the viewport, so the
              blame column has a right edge to stick to.

              the band colours are opaque on purpose - the sticky blame column inherits
              them to hide code scrolling underneath, and a translucent colour would
              let it show through
            */
            class={cn(
              'code-row w-max min-w-full flex',
              blame() && (row()?.band === 1 ? 'bg-muted' : 'bg-background'),
              comment() && hovered() === comment() && 'bg-accent/30',
              props.activeLine === i() + 1 && 'bg-accent/40',
              props.currentLine === i() + 1 && 'bg-accent/60',
            )}
          >
            <span class="line-number shrink-0 bg-inherit" style={{ 'min-width': gutterWidth() }}>
              <a
                href={props.lineHref?.(i() + 1)}
                onClick={(event) => {
                  // ctrl/middle-click stays a link, the way code search's gutter is
                  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return
                  event.preventDefault()
                  props.onLineClick(i() + 1)
                }}
              >
                {i() + 1}
              </a>
            </span>

            <span class={cn('code-cell flex-none', props.columnGuide && 'column-guide')}>
              <Show when={comment()} fallback={codeLine()}>
                {block => (
                  <HoverCard>
                    <HoverCardTrigger as="span" class="cursor-help">
                      {codeLine()}
                    </HoverCardTrigger>
                    <HoverCardContent class="markdown max-h-100 overflow-auto whitespace-normal font-sans">
                      {/* eslint-disable-next-line solid/no-innerhtml -- sanitized server-side, see services/comments.ts */}
                      <div innerHTML={block().html} />
                    </HoverCardContent>
                  </HoverCard>
                )}
              </Show>
            </span>

            {/* blame sits to the right of the code, the way code search has it, and
                stays pinned there while long lines scroll underneath */}
            <Show when={blame()}>
              <span class="sticky right-0 ml-auto w-64 flex shrink-0 select-none gap-2 border-l border-border bg-inherit pl-2">
                <Show when={row()?.starts ? row() : undefined}>
                  {entry => (
                    <>
                      <CommitPopover
                        repo={props.repo}
                        hunk={entry().hunk}
                        ordinal={entry().ordinal}
                        remote={props.remote}
                        onBlameBefore={sha => props.onBlameBefore?.(sha)}
                      />
                      <span class="w-20 shrink-0 text-muted-foreground tabular-nums">
                        {isoDate(entry().hunk.time)}
                      </span>
                      <span class="min-w-0 flex-1 truncate text-muted-foreground">
                        {entry().hunk.email || entry().hunk.author}
                      </span>
                    </>
                  )}
                </Show>
              </span>
            </Show>
          </div>
        )
      }}
    </VList>
  )
}
